import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Card as CardData } from '../types'
import Card from './Card'
import GlossyCard from './GlossyCard'
import { getCardDisplayNameForCard, getCardSummaryForType, getTypeIconForType } from '../constants'
import { isTouchDevice } from '../services/device'
import { Language, t } from '../data/translations'

const StashChoiceModal: React.FC<{
  options: CardData[];
  onChoose: (cardId: string, fromBurned?: boolean) => void;
  onMouseEnterApp?: (card: CardData, rotation: number, source: string) => void;
  onMouseLeaveApp?: () => void;
  viewerIsOccultist?: boolean;
  burnedStashCards?: CardData[];
  heroId?: string;
  maxSelection?: number;
  language: Language;
}> = ({
  options,
  onChoose,
  onMouseEnterApp,
  onMouseLeaveApp,
  viewerIsOccultist = false,
  burnedStashCards = [],
  heroId,
  maxSelection = 1,
  language
}) => {
    // console.log('StashChoiceModal rendered', { maxSelection, optionsCount: options.length, viewMode: 'initial' });
    const [viewMode, setViewMode] = useState<'STASH' | 'BURNED'>('STASH')
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [focusedCardId, setFocusedCardId] = useState<string | null>(null)
    const [vibratingCardId, setVibratingCardId] = useState<string | null>(null)

    const hoverTimerRef = useRef<number | null>(null)
    const holdTimerRef = useRef<number | null>(null)
    const isLongPressRef = useRef<boolean>(false)
    const isPressingRef = useRef<boolean>(false)

    const isFerrim = heroId === 'FERRIM'
    const canViewBurned = isFerrim && burnedStashCards.length > 0

    const displayedCards = viewMode === 'STASH' ? options : burnedStashCards.slice(0, 15)

    // Reset selection when view mode changes
    useEffect(() => {
      setSelectedIds([])
      setFocusedCardId(null)
    }, [viewMode])

    const clearTimers = () => {
      if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
      if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    }

    const handleCardMouseDown = (e: React.MouseEvent, cardId: string) => {
      e.stopPropagation()
      clearTimers()
      isLongPressRef.current = false
      isPressingRef.current = true

      const isTouch = isTouchDevice()

      // On touch devices: long press (600ms) triggers focus mode for already-selected cards
      // On mouse: short hold (200ms) triggers focus
      const holdDuration = isTouch ? 600 : 200

      holdTimerRef.current = window.setTimeout(() => {
        isLongPressRef.current = true

        if (isTouch) {
          // On touch: long press only focuses if already selected, otherwise just select
          if (selectedIds.includes(cardId)) {
            setFocusedCardId(cardId)
          } else {
            selectCard(cardId)
            triggerVibration(cardId)
          }
        } else {
          // Mouse: original behavior - select and focus
          if (!selectedIds.includes(cardId)) {
            selectCard(cardId)
          }
          setFocusedCardId(cardId)
        }
      }, holdDuration)
    }

    const handleCardMouseUp = (e: React.MouseEvent, cardId: string) => {
      e.stopPropagation()
      clearTimers()
      isPressingRef.current = false

      if (isLongPressRef.current) {
        // Was holding - clear focus on release
        setFocusedCardId(null)
        isLongPressRef.current = false
        return
      }

      const isTouch = isTouchDevice()

      if (isTouch) {
        // Touch: first tap selects, second tap on selected card opens focus
        if (selectedIds.includes(cardId)) {
          // Already selected - toggle focus or deselect
          if (focusedCardId === cardId) {
            // Currently focused - deselect
            deselectCard(cardId)
            setFocusedCardId(null)
          } else {
            // Selected but not focused - open focus preview
            setFocusedCardId(cardId)
          }
        } else {
          // Not selected - select it
          selectCard(cardId)
          triggerVibration(cardId)
        }
      } else {
        // Mouse: original behavior
        if (selectedIds.includes(cardId)) {
          deselectCard(cardId)
          setFocusedCardId(null)
        } else {
          selectCard(cardId)
          triggerVibration(cardId)
          startHoverTimer(cardId)
        }
      }
    }

    const selectCard = (cardId: string) => {
      setSelectedIds(prev => {
        if (prev.includes(cardId)) return prev
        if (prev.length < maxSelection) {
          return [...prev, cardId]
        } else {
          // Replace oldest (FIFO)
          return [...prev.slice(1), cardId]
        }
      })
    }

    const deselectCard = (cardId: string) => {
      setSelectedIds(prev => prev.filter(id => id !== cardId))
    }

    const triggerVibration = (cardId: string) => {
      setVibratingCardId(null) // Reset just in case to allow re-trigger
      requestAnimationFrame(() => {
        setVibratingCardId(cardId)
        setTimeout(() => setVibratingCardId(null), 400)
      })
    }

    const startHoverTimer = (cardId: string) => {
      clearTimers()
      hoverTimerRef.current = window.setTimeout(() => {
        setFocusedCardId(cardId)
      }, 600)
    }

    const handleCardMouseEnter = (cardId: string) => {
      startHoverTimer(cardId)
    }

    const handleCardMouseLeave = () => {
      // If pressing, don't clear focus (prevents blip)
      if (isPressingRef.current) return

      clearTimers()
      setFocusedCardId(null)
      isLongPressRef.current = false
    }

    const handleBackgroundClick = () => {
      setSelectedIds([])
      setFocusedCardId(null)
    }

    const handleConfirm = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (selectedIds.length === maxSelection) {
        selectedIds.forEach(id => onChoose(id, viewMode === 'BURNED'))
        setSelectedIds([])
      }
    }

    const getCardDescription = (card: CardData, alignLeft: boolean) => {
      const type = card.type
      const summary = getCardSummaryForType(type, (card as any).faceSlug, language)
      const special: string[] = []

      if (card.essenceCost) special.push(`${t('term.essence', language)} ${card.essenceCost}`)
      if (card.extendedShield) special.push(t('term.shieldReaction', language))
      if (card.playableAfterPass) special.push(t('term.playableAfterPass', language))

      return (
        <div className={`flex flex-col ${alignLeft ? 'items-start' : 'items-end'} gap-3 animate-fadeIn w-full`}>
          <div className={`font-bold text-amber-400 text-4xl text-shadow-lg flex items-center gap-3 border-b border-amber-500/30 pb-2 mb-2 w-full ${alignLeft ? 'justify-start' : 'justify-end'}`}>
            {getCardDescriptionTitle(card)}
          </div>
          <div className={`text-slate-200 text-xl ${alignLeft ? 'text-left' : 'text-right'} leading-relaxed w-full`}>
            <p>{summary}</p>
            {special.length > 0 && <p className="text-lg text-amber-300 mt-3 font-semibold">{special.join(' • ')}</p>}
          </div>
        </div>
      )
    }

    const getCardDescriptionTitle = (card: CardData) => {
      return getCardDisplayNameForCard(card, 0, language);
    }

    return (
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-40"
        onClick={handleBackgroundClick}
      >
        {/* Dimmer Overlay for Focus Mode */}
        {focusedCardId && (
          <div className="absolute inset-0 bg-black/60 z-40 transition-opacity duration-300 pointer-events-none" />
        )}

        {/* Close button for Focus Mode */}
        {focusedCardId && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCardMouseLeave();
            }}
            className="absolute top-6 right-6 w-12 h-12 bg-black/60 hover:bg-slate-800 border-2 border-slate-500 rounded-full flex items-center justify-center text-slate-300 hover:text-white z-50 transition-all shadow-[0_0_15px_rgba(0,0,0,0.5)] transform hover:scale-110 active:scale-95"
            aria-label={t('ui.cancel', language)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <h2 className={`text-4xl font-bold mb-4 text-amber-400 text-shadow-lg pointer-events-none transition-opacity duration-300 -translate-y-[30px] ${focusedCardId ? 'opacity-20' : 'opacity-100'}`}>
          {viewMode === 'STASH' ? t('ui.stashShop', language) : t('ui.resurrectFromAsh', language)}
        </h2>

        <div className={`text-slate-300 mb-6 pointer-events-none transition-opacity duration-300 -translate-y-[30px] ${focusedCardId ? 'opacity-20' : 'opacity-100'}`}>
          {t('ui.selectCardCount', language, { count: maxSelection.toString() })}
        </div>

        {canViewBurned && (
          <div className={`flex gap-4 mb-6 transition-opacity duration-300 ${focusedCardId ? 'opacity-20' : 'opacity-100'}`} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setViewMode('STASH')}
              className={`px-6 py-2 rounded-full font-bold transition-all ${viewMode === 'STASH' ? 'bg-amber-500 text-black' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              {t('ui.stashOptions', language)}
            </button>
            <button
              onClick={() => setViewMode('BURNED')}
              className={`px-6 py-2 rounded-full font-bold transition-all ${viewMode === 'BURNED' ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              {t('ui.burnedPile', language)} ({burnedStashCards.length})
            </button>
          </div>
        )}

        <div
          className={`flex flex-wrap items-center justify-center gap-8 ${viewMode === 'BURNED' ? 'max-w-5xl' : ''} px-10 pb-20`}
          onClick={e => e.stopPropagation()}
        >
          {displayedCards.map((card, index) => {
            const isSelected = selectedIds.includes(card.id)
            const isFocused = focusedCardId === card.id

            // Determine scale and opacity classes
            let containerClass = 'transition-all duration-300 mx-4 '

            if (isFocused) {
              // Focused: Huge scale, high z-index, full opacity
              containerClass += 'scale-[2.5] z-50 opacity-100'
            } else if (focusedCardId) {
              // Others when one is focused: Dimmed heavily
              containerClass += 'scale-100 z-10 opacity-10 blur-[2px] grayscale'
            } else if (isSelected) {
              // Selected (no focus): Larger, z-30, add slight hover zoom
              containerClass += 'scale-150 hover:scale-[1.6] z-30 opacity-100'
            } else {
              // Normal: Hover effect
              containerClass += 'scale-100 hover:scale-110 z-20 opacity-80 hover:opacity-100'
            }

            return (
              <div
                key={card.id}
                className={`relative cursor-pointer ${containerClass} touch-none`} // touch-none prevents scrolling/gestures
                onMouseDown={(e) => handleCardMouseDown(e, card.id)}
                onMouseUp={(e) => handleCardMouseUp(e, card.id)}
                onMouseEnter={() => {
                  handleCardMouseEnter(card.id)
                  if (onMouseEnterApp) onMouseEnterApp(card, 0, 'stash-choice')
                }}
                onMouseLeave={() => {
                  handleCardMouseLeave()
                  if (onMouseLeaveApp) onMouseLeaveApp()
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className={`transition-all duration-300 ${vibratingCardId === card.id ? 'animate-vibrate' : ''}`}>
                  <GlossyCard
                    isActive={isFocused}
                    isElectric={isSelected}
                    electricColor="#7df9ff"
                  >
                    <Card
                      card={card}
                      isClickable={true}
                      isActivating={isFocused}
                      onClick={() => { }}
                      viewerIsOccultist={viewerIsOccultist}
                      source="stash-choice"
                      disableHoverScale={true}
                      language={language}
                    />
                  </GlossyCard>
                </div>

                {isFocused && (
                  <div className={`absolute top-1/2 ${index < 3 ? 'left-full ml-4 flex justify-start origin-left' : 'right-full mr-4 flex justify-end origin-right'} -translate-y-1/2 pointer-events-none z-50`}>
                    {/*
                      Description Container
                      - Scale 0.4 to counteract parent 2.5x
                      - Width fixed to ensure text wrapping
                      - Positioned to the left/right of the card
                    */}
                    <div className={`transform scale-[0.4] ${index < 3 ? 'origin-left' : 'origin-right'} w-[500px] bg-black/90 p-6 rounded-2xl border-2 border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.3)] backdrop-blur-xl`}>
                      {getCardDescription(card, index < 3)}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className={`fixed bottom-[70px] z-50 transition-opacity duration-300 ${focusedCardId ? 'opacity-20 pointer-events-none' : 'opacity-100'}`} onClick={e => e.stopPropagation()}>
          <button
            onClick={handleConfirm}
            disabled={selectedIds.length !== maxSelection}
            className={`px-12 py-4 rounded-xl font-bold text-2xl shadow-xl transition-all duration-300 transform ${selectedIds.length === maxSelection
              ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white scale-110 hover:scale-115 hover:shadow-amber-500/50 cursor-pointer'
              : 'bg-slate-800 text-slate-500 cursor-not-allowed grayscale'
              }`}
          >
            {t('ui.confirmChoice', language)}
          </button>
        </div>
      </div>
    )
  }

export default StashChoiceModal
